import { useState, useEffect, useMemo, useRef } from 'react';
import { useGetDetailMetadataQuery, useGetEntityQuery, gridApi } from '../../grid/api/gridApi';
import { useAppDispatch } from '../../../app/store';
import type { LookupDataItem } from '../../grid/types';
import type { DetailMetadata } from '../types';
import { getInitialFieldValue } from '../../grid/utils/booleanFieldUtils';
import { loadRelationLookupData } from '../lookupDataLoader';
import type { ProjectScopedAllowedIds } from '../lookupDataLoader';
import { getProjectIdFromEntity } from '../lookupUtils';
import { isActiveEmployeeRow } from '../employeeActivityUtils';
import api from '../../../services/api';
import { extractErrorMessage } from '../../../utils/errorUtils';

const FINANCE_EMAIL_HISTORY_DATA_SOURCE = 'CrmFinanceEmailHistory';

type ProjectScopedLookupOptions = {
  developersNodeKey?: string;
};

type ProjectScopedIdsCacheEntry = {
  value: ProjectScopedAllowedIds;
  cachedAt: number;
};

const PROJECT_SCOPED_IDS_CACHE_TTL_MS = 5 * 60 * 1000;
const projectScopedIdsCache = new Map<string, ProjectScopedIdsCacheEntry>();

const getProjectScopedCacheKey = (projectId: number, developersNodeKey: string): string => {
  return `${projectId}:${developersNodeKey}`;
};

const getCachedProjectScopedIds = (cacheKey: string): ProjectScopedAllowedIds | undefined => {
  const cached = projectScopedIdsCache.get(cacheKey);
  if (!cached) return undefined;

  const isExpired = Date.now() - cached.cachedAt > PROJECT_SCOPED_IDS_CACHE_TTL_MS;
  if (isExpired) {
    projectScopedIdsCache.delete(cacheKey);
    return undefined;
  }

  return cached.value;
};

const setCachedProjectScopedIds = (cacheKey: string, value: ProjectScopedAllowedIds): void => {
  projectScopedIdsCache.set(cacheKey, {
    value,
    cachedAt: Date.now(),
  });
};

const toPositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return null;
};

const extractProjectReleases = (payload: unknown): Array<Record<string, unknown>> => {
  const root = payload as Record<string, unknown>;
  const container =
    (root?.data as Record<string, unknown> | undefined) ||
    (root?.Data as Record<string, unknown> | undefined) ||
    root;

  const releases =
    (container?.projectReleases as Array<Record<string, unknown>> | undefined) ||
    (container?.ProjectReleases as Array<Record<string, unknown>> | undefined) ||
    [];

  return Array.isArray(releases) ? releases : [];
};

const extractEmployeeRows = (payload: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(payload)) {
    return payload as Array<Record<string, unknown>>;
  }

  const root = payload as Record<string, unknown>;
  const data = root?.data || root?.Data;
  return Array.isArray(data) ? (data as Array<Record<string, unknown>>) : [];
};

type UseDetailFormDataParams = {
  detailFormId?: number;
  detailFormKey?: string;
  entityId?: string | number;
  mode?: 'add' | 'edit';
  skip?: boolean;
  /** Grid content key (not detail form key) — used to invalidate list cache after GetOne side effects. */
  gridNodeKey?: string;
  projectScopedLookupOptions?: ProjectScopedLookupOptions;
};

type UseDetailFormDataResult = {
  detailMetadata: DetailMetadata | undefined;
  entity: Record<string, unknown> | undefined;
  initialValues: Record<string, unknown>;
  lookupMaps: Record<string, Record<string, string>>;
  lookupOptionMaps: Record<string, Record<string, string>>;
  lookupDataArrays: Record<string, LookupDataItem[]>;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
};

export const useDetailFormData = ({
  detailFormId,
  detailFormKey,
  entityId,
  mode = 'edit',
  skip = false,
  gridNodeKey,
  projectScopedLookupOptions,
}: UseDetailFormDataParams): UseDetailFormDataResult => {
  const dispatch = useAppDispatch();
  const invalidatedFinanceEmailIdsRef = useRef<Set<string>>(new Set());
  const previousEntityQueryKeyRef = useRef<string | undefined>(undefined);

  const {
    data: detailMetadata,
    isLoading: metadataLoading,
    isFetching: metadataFetching,
    error: metadataError,
  } = useGetDetailMetadataQuery(
    { detailFormId, detailFormKey },
    { skip: skip || (!detailFormId && !detailFormKey) }
  );

  const entityIdStr = entityId != null ? String(entityId) : '';
  const entityQueryKey = `${detailMetadata?.contentKey ?? ''}:${entityIdStr}`;
  const isEntityQueryChanging = previousEntityQueryKeyRef.current !== entityQueryKey;

  const {
    data: entity,
    isLoading: entityLoading,
    isFetching: entityFetching,
    error: entityError,
  } = useGetEntityQuery(
    { nodeKey: detailMetadata?.contentKey ?? '', id: entityIdStr },
    {
      skip: skip || mode === 'add' || !detailMetadata?.contentKey || !entityIdStr,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (entityQueryKey && entity && !entityFetching) {
      previousEntityQueryKeyRef.current = entityQueryKey;
    }
  }, [entityQueryKey, entity, entityFetching]);

  // GetOne may mark finance emails as read. Invalidate grid cache.
  useEffect(() => {
    if (skip || mode !== 'edit' || !entity || !entityIdStr) {
      return;
    }

    const dataSource = detailMetadata?.dataSource;
    if (
      !dataSource ||
      dataSource.toLowerCase() !== FINANCE_EMAIL_HISTORY_DATA_SOURCE.toLowerCase()
    ) {
      return;
    }

    const cacheKey = `${gridNodeKey ?? ''}:${entityIdStr}`;
    if (invalidatedFinanceEmailIdsRef.current.has(cacheKey)) {
      return;
    }
    invalidatedFinanceEmailIdsRef.current.add(cacheKey);

    if (gridNodeKey) {
      dispatch(gridApi.util.invalidateTags([{ type: 'GridData', id: `node:${gridNodeKey}` }]));
    } else {
      dispatch(gridApi.util.invalidateTags(['GridData']));
    }
  }, [skip, mode, entity, entityIdStr, gridNodeKey, detailMetadata?.dataSource, dispatch]);

  const [lookupMaps, setLookupMaps] = useState<Record<string, Record<string, string>>>({});
  const [lookupOptionMaps, setLookupOptionMaps] = useState<Record<string, Record<string, string>>>(
    {}
  );
  const [lookupDataArrays, setLookupDataArrays] = useState<Record<string, LookupDataItem[]>>({});
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupsInitialized, setLookupsInitialized] = useState(false);
  const [projectScopedAllowedIds, setProjectScopedAllowedIds] = useState<ProjectScopedAllowedIds>();
  const [projectScopedLoading, setProjectScopedLoading] = useState(false);

  useEffect(() => {
    if (
      skip ||
      mode !== 'edit' ||
      !entity ||
      !projectScopedLookupOptions?.developersNodeKey ||
      !detailMetadata
    ) {
      setProjectScopedAllowedIds(undefined);
      setProjectScopedLoading(false);
      return;
    }

    const projectId = getProjectIdFromEntity(entity as Record<string, unknown>);
    if (!projectId) {
      setProjectScopedAllowedIds(undefined);
      setProjectScopedLoading(false);
      return;
    }

    const cacheKey = getProjectScopedCacheKey(
      projectId,
      projectScopedLookupOptions.developersNodeKey
    );
    const cached = getCachedProjectScopedIds(cacheKey);
    if (cached) {
      setProjectScopedAllowedIds(cached);
      setProjectScopedLoading(false);
      return;
    }

    let cancelled = false;
    const fetchProjectScopedLookupIds = async () => {
      setProjectScopedLoading(true);

      try {
        const [projectResponse, developersResponse] = await Promise.all([
          api.get('/umbraco/api/Project/GetProjectByIdAsync', {
            params: { projectId },
          }),
          api.post(
            '/umbraco/api/Grid/GetGridDataWithParametersAsync?nodeKey=' +
              projectScopedLookupOptions.developersNodeKey,
            { ProjectId: projectId }
          ),
        ]);

        const releaseIds = extractProjectReleases(projectResponse.data)
          .map((release) =>
            toPositiveNumber(
              release.releaseId ?? release.ReleaseId ?? release.projectReleasesId ?? release.Id
            )
          )
          .filter((id): id is number => id !== null);

        const employeeIds = extractEmployeeRows(developersResponse.data)
          .filter(isActiveEmployeeRow)
          .map((employee) =>
            toPositiveNumber(
              employee.crmRelationId ?? employee.CrmRelationId ?? employee.id ?? employee.Id
            )
          )
          .filter((id): id is number => id !== null);

        if (!cancelled) {
          const nextValue = { releaseIds, employeeIds };
          setCachedProjectScopedIds(cacheKey, nextValue);
          setProjectScopedAllowedIds(nextValue);
        }
      } catch {
        if (!cancelled) {
          setProjectScopedAllowedIds(undefined);
        }
      } finally {
        if (!cancelled) {
          setProjectScopedLoading(false);
        }
      }
    };

    fetchProjectScopedLookupIds();

    return () => {
      cancelled = true;
    };
  }, [skip, mode, entity, detailMetadata, projectScopedLookupOptions?.developersNodeKey]);

  useEffect(() => {
    if (skip || !detailMetadata?.fields || !detailMetadata?.contentKey) {
      setLookupLoading(false); // guard: metadata not ready or skipped
      return;
    }

    if (mode === 'edit' && !entity) {
      setLookupLoading(false); // guard: entity not yet loaded in edit mode
      return;
    }

    if (projectScopedLoading) {
      // waiting for project-scoped ids — don't reset, will re-run when ready
      return;
    }

    const fetchLookups = async () => {
      setLookupLoading(true);
      try {
        const { maps, displayMaps, arrays } = await loadRelationLookupData({
          dispatch,
          fields: detailMetadata.fields,
          contentKey: detailMetadata.contentKey,
          mode,
          parentId: mode === 'edit' && entityIdStr ? entityIdStr : undefined,
          entity: entity as Record<string, unknown> | undefined,
          projectScopedAllowedIds,
          logPrefix: 'useDetailFormData',
        });

        if (cancelled) return;
        setLookupMaps(displayMaps);
        setLookupOptionMaps(maps);
        setLookupDataArrays(arrays);
        setLookupsInitialized(true);
      } finally {
        if (!cancelled) setLookupLoading(false);
      }
    };

    let cancelled = false;
    fetchLookups().catch((error) => {
      if (!cancelled) {
        console.error('[useDetailFormData] fetchLookups failed:', error);
        setLookupLoading(false);
      }
    });

    return () => {
      cancelled = true;
      setLookupLoading(false); // reset on cancellation so next render starts clean
    };
  }, [
    detailMetadata,
    entity,
    mode,
    skip,
    projectScopedAllowedIds,
    projectScopedLoading,
    dispatch,
    entityIdStr,
  ]);

  const initialValues = useMemo<Record<string, unknown>>(() => {
    if (mode === 'edit' && entity) {
      return entity as Record<string, unknown>;
    }

    return Object.fromEntries(
      (detailMetadata?.fields ?? [])
        .filter((field): field is typeof field & { dataField: string } => !!field.dataField)
        .map((field) => [field.dataField, getInitialFieldValue(field)])
    );
  }, [mode, entity, detailMetadata]);

  const errorMessage = metadataError
    ? extractErrorMessage(metadataError, 'Failed to load detail form metadata.')
    : mode === 'edit' && entityError
      ? extractErrorMessage(entityError, 'Failed to load detail entity.')
      : undefined;

  return {
    detailMetadata,
    entity: entity as Record<string, unknown> | undefined,
    initialValues,
    lookupMaps,
    lookupOptionMaps,
    lookupDataArrays,
    isLoading:
      (metadataLoading && !detailMetadata) ||
      metadataFetching ||
      (mode === 'edit' && entityLoading && !entity) ||
      (mode === 'edit' && entityFetching && isEntityQueryChanging) ||
      projectScopedLoading ||
      (lookupLoading && !lookupsInitialized),
    isError: Boolean(metadataError || (mode === 'edit' && entityError)),
    errorMessage,
  };
};
